import React from 'react';
import styled from 'styled-components';
import Grid from '@material-ui/core/Grid';
import SGTitle from 'components/Typography/SGTitle';
import SGBody from 'components/Typography/SGBody';
import Device from 'features/POSRegisters/interfaces/Device';
// import PerformanceTrack from 'features/POSRegisters/POSTable/Registers/components/PerformanceTrack';
import {CfdScanDetails} from 'features/POSRegisters/interfaces/ScanDetails';
import Alert from 'features/POSRegisters/components/Alert';
import getSubTypeAlerts from 'features/POSRegisters/functions/getSubTypeAlerts';
import {AlertScanSubType} from 'features/POSRegisters/interfaces/AlertInfo';

//#region styles
const StyledContainer = styled(Grid)`
  display: grid;
  grid-template-columns: 1fr;
  padding-top: 8px;
  gap: 16px;
`;

const StyledGrid = styled(Grid)`
  display: grid;
  grid-template-columns: 10rem auto 1fr;
  gap: 16px;
  align-items: center;
  color: ${props => props.theme.text};
  background: ${props => props.theme.disabledStateBlue}33;
  overflow-x: auto;
`;

const StyledRightAlignedText = styled(SGTitle)`
  text-align: right;
`;

const StyledDivider = styled.div`
  height: 100%;
  border-left: 1px solid ${props => props.theme.grey};
`;

const StyledDetails = styled(Grid)`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  margin: 8px 0;
  padding-right: 8px;
`;

const StyledDetailsRow = styled(Grid)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, auto));
  grid-template-rows: auto;
  grid-auto-flow: row;
  gap: 16px;
  justify-content: start;
`;

const StyledDetail = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  margin: 0;
  text-align: left;
  gap: 4px;
`;

const StyledLabel = styled(SGTitle)`
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  color: ${props => props.theme.disabledStateBlue};
`;

const StyledDiskInformation = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  flex-grow: 1;
  width: 100%;
`;
//#endregion styles

export default function IpadMiniSummary(ipadMini: Device<CfdScanDetails>) {
  const {
    pingStatus,
    ipAddress,
    macAddress,
    vlanNumber,
    model,
    manufacturer,
    serialNumber,
    switchId,
    portNumber,
    switchNumber,
    consumerFacingDisplayAppVersion,
    iosVersion,
    mobileApiLastSeen,
    consumerFacingDisplayFirstSeenDate,
    consumerFacingDisplayLastSeenDate,
    deviceName,
  } = ipadMini.scanResults ?? {};

  return (
    <StyledContainer>
      <StyledGrid>
        <StyledRightAlignedText className="text-color t5">CONNECTIVITY</StyledRightAlignedText>
        <StyledDivider />
        <StyledDetails>
          <StyledDetailsRow>
            <StyledDetail>
              <StyledLabel className="t6">PING STATUS</StyledLabel>
              <SGBody className="b2">{pingStatus ?? 'Failure'}</SGBody>
            </StyledDetail>
            <StyledDetail>
              <StyledLabel className="t6">IP ADDRESS</StyledLabel>
              <SGBody className="b2">{ipAddress}</SGBody>
            </StyledDetail>
            <StyledDetail>
              <StyledLabel className="t6">MAC ADDRESS</StyledLabel>
              <SGBody className="b2">{macAddress}</SGBody>
            </StyledDetail>
            <StyledDetail>
              <StyledLabel className="t6">Vlan</StyledLabel>
              <SGBody className="b2">{vlanNumber}</SGBody>
            </StyledDetail>
            <StyledDetail>
              <StyledLabel className="t6">Switch ID</StyledLabel>
              <SGBody className="b2">{switchId}</SGBody>
            </StyledDetail>
            <StyledDetail>
              <StyledLabel className="t6">Switch Number</StyledLabel>
              <SGBody className="b2">{switchNumber}</SGBody>
            </StyledDetail>
            <StyledDetail>
              <StyledLabel className="t6">Port Number</StyledLabel>
              <SGBody className="b2">{portNumber}</SGBody>
            </StyledDetail>
          </StyledDetailsRow>

          {getSubTypeAlerts(ipadMini.alerts, AlertScanSubType.CONNECTIVITY).map(
            (alert, alertIdx) => (
              <Alert
                key={`${deviceName}-${alertIdx}`}
                device={ipadMini}
                alert={alert}
                rows={[
                  {label: 'Issue', value: alert.description, fullWidth: true},
                  {label: 'Effect', value: alert.effect, fullWidth: true},
                  {label: 'Solution', value: `${alert.solution} ${alert.kb}`, fullWidth: true},
                ]}
              />
            )
          )}
        </StyledDetails>
      </StyledGrid>
      <StyledGrid>
        <StyledRightAlignedText className="text-color t5">CHECK-IN STATUS</StyledRightAlignedText>
        <StyledDivider />
        <StyledDetails>
          <StyledDetailsRow>
            <StyledDetail>
              <StyledLabel className="t6">Mobile API Last Seen</StyledLabel>
              <SGBody className="b2">{mobileApiLastSeen}</SGBody>
            </StyledDetail>
            <StyledDetail>
              <StyledLabel className="t6">CFD First Seen</StyledLabel>
              <SGBody className="b2">{consumerFacingDisplayFirstSeenDate}</SGBody>
            </StyledDetail>
            <StyledDetail>
              <StyledLabel className="t6">CFD Last Seen</StyledLabel>
              <SGBody className="b2">{consumerFacingDisplayLastSeenDate}</SGBody>
            </StyledDetail>
          </StyledDetailsRow>

          {getSubTypeAlerts(ipadMini.alerts, AlertScanSubType.CHECK_IN_STATUS).map(
            (alert, alertIdx) => (
              <Alert
                key={`${deviceName}-${alertIdx}`}
                device={ipadMini}
                alert={alert}
                rows={[
                  {label: 'Issue', value: alert.description, fullWidth: true},
                  {label: 'Effect', value: alert.effect, fullWidth: true},
                  {label: 'Solution', value: `${alert.solution} ${alert.kb}`, fullWidth: true},
                ]}
              />
            )
          )}
        </StyledDetails>
      </StyledGrid>
      {/* <StyledGrid>
        <StyledRightAlignedText className="text-color t5">PERFORMANCE</StyledRightAlignedText>
        <StyledDivider />
        <StyledDetails>
          <SGTitle className="text-color t5">DISK</SGTitle>
          <StyledDetail>
            <StyledDiskInformation>
              <SGBody className="b2">Macintosh HD (Boot Partition)</SGBody>
              <SGBody className="b2">14.56 GB out of 64 GB (22%)</SGBody>
            </StyledDiskInformation>
            <PerformanceTrack value={40} />
          </StyledDetail>
        </StyledDetails>
      </StyledGrid> */}
      <StyledGrid>
        <StyledRightAlignedText className="text-color t5">INFORMATION</StyledRightAlignedText>
        <StyledDivider />
        <StyledDetails>
          <SGTitle className="text-color t5">NPOS VERSION</SGTitle>
          <StyledDetailsRow>
            <StyledDetail>
              <StyledLabel className="t6">VERSION</StyledLabel>
              <SGBody className="b2">{consumerFacingDisplayAppVersion}</SGBody>
            </StyledDetail>
          </StyledDetailsRow>

          {getSubTypeAlerts(ipadMini.alerts, AlertScanSubType.NPOS).map((alert, alertIdx) => (
            <Alert
              key={`${deviceName}-${alertIdx}`}
              device={ipadMini}
              alert={alert}
              rows={[
                {label: 'Issue', value: alert.description, fullWidth: true},
                {label: 'Effect', value: alert.effect, fullWidth: true},
                {label: 'Solution', value: `${alert.solution} ${alert.kb}`, fullWidth: true},
              ]}
            />
          ))}
          <SGTitle className="text-color t5">OPERATING SYSTEM</SGTitle>
          <StyledDetailsRow>
            <StyledDetail>
              <StyledLabel className="t6">iOS Version</StyledLabel>
              <SGBody className="b2">{iosVersion}</SGBody>
            </StyledDetail>
          </StyledDetailsRow>

          {getSubTypeAlerts(ipadMini.alerts, AlertScanSubType.OPERATING_SYSTEM).map(
            (alert, alertIdx) => (
              <Alert
                key={`${deviceName}-${alertIdx}`}
                device={ipadMini}
                alert={alert}
                rows={[
                  {label: 'Issue', value: alert.description, fullWidth: true},
                  {label: 'Effect', value: alert.effect, fullWidth: true},
                  {label: 'Solution', value: `${alert.solution} ${alert.kb}`, fullWidth: true},
                ]}
              />
            )
          )}
          <SGTitle className="text-color t5">HARDWARE</SGTitle>
          <StyledDetailsRow>
            <StyledDetail>
              <StyledLabel className="t6">DEVICE NAME</StyledLabel>
              <SGBody className="b2">{deviceName}</SGBody>
            </StyledDetail>
            <StyledDetail>
              <StyledLabel className="t6">Manufacturer</StyledLabel>
              <SGBody className="b2">{manufacturer}</SGBody>
            </StyledDetail>
            <StyledDetail>
              <StyledLabel className="t6">MODEL</StyledLabel>
              <SGBody className="b2">{model}</SGBody>
            </StyledDetail>
            <StyledDetail>
              <StyledLabel className="t6">PRIMARY MAC ADDRESS</StyledLabel>
              <SGBody className="b2">{macAddress}</SGBody>
            </StyledDetail>
            <StyledDetail>
              <StyledLabel className="t6">SERIAL NUMBER</StyledLabel>
              <SGBody className="b2">{serialNumber}</SGBody>
            </StyledDetail>
            <StyledDetail>
              <StyledLabel className="t6">IP ADDRESS</StyledLabel>
              <SGBody className="b2">{ipAddress}</SGBody>
            </StyledDetail>
          </StyledDetailsRow>

          {getSubTypeAlerts(ipadMini.alerts, AlertScanSubType.HARDWARE).map((alert, alertIdx) => (
            <Alert
              key={`${deviceName}-${alertIdx}`}
              device={ipadMini}
              alert={alert}
              rows={[
                {label: 'Issue', value: alert.description, fullWidth: true},
                {label: 'Effect', value: alert.effect, fullWidth: true},
                {label: 'Solution', value: `${alert.solution} ${alert.kb}`, fullWidth: true},
              ]}
            />
          ))}
        </StyledDetails>
      </StyledGrid>
    </StyledContainer>
  );
}
